import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConfigureAssignmentModal from '../../components/tasks/ConfigureAssignmentModal';
import BulkReassignModal from '../../components/tasks/BulkReassignModal';
import { ActionTipButton } from '../../components/common/ActionTipButton';
import LinkedCasesActionButton from '../../components/tasks/LinkedCasesActionButton';
import LinkedCasesModal from '../../components/tasks/LinkedCasesModal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { DataTable, type Column } from '../../components/table/DataTable';
import type { AssignTaskRow } from '../../data/tasks';
import {
  fetchAssignTaskColumnSuggestions,
  bulkReallocateCaseRequests,
  listAllAssignedTasks,
  listAssignedTasks,
  listReallocationRequests,
  listUnassignedTasks,
  type AssignTasksTab,
} from '../../services/taskAssignment';
import { setFlashToast } from '../../utils/flashToast';
import {
  assignCategoryAllocationUser,
  getManualAllocationMode,
  roundRobinAssignUsers,
} from '../../services/caseRequestCategoryAllocation';
import { ensureExemptSubFileCategoryIdsLoaded } from '../../utils/subFileCategoryRules';
import { useTaskCaseDetailsNavigation } from '../../hooks/useTaskCaseDetailsNavigation';
import { assignTasksReturn } from '../task-case/taskCaseDetailsReturnNavigation';
import { notificationRowHighlightClass } from '../../utils/taskRowHighlight';

const MONTH_MAP: Record<string, string> = {
  jan: '01',
  feb: '02',
  mar: '03',
  apr: '04',
  may: '05',
  jun: '06',
  jul: '07',
  aug: '08',
  sep: '09',
  oct: '10',
  nov: '11',
  dec: '12',
};

function submittedAtToIsoDate(submittedAt: string): string {
  // Expected label format (mock + API currently): "15 Jan 2026, 10:30 AM"
  const m = submittedAt.trim().match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})\b/);
  if (!m) return '';
  const dd = m[1].padStart(2, '0');
  const mm = MONTH_MAP[m[2].toLowerCase()] ?? '';
  const yyyy = m[3];
  if (!mm) return '';
  return `${yyyy}-${mm}-${dd}`;
}

function hasActiveColumnFilters(filters: Record<string, string>): boolean {
  return Object.values(filters).some((value) => String(value ?? '').trim().length > 0);
}

export default function AssignTasksPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTabParam = searchParams.get('tab');
  const initialTab: AssignTasksTab =
    initialTabParam === 'assigned' || initialTabParam === 'reallocation' ? initialTabParam : 'unassigned';
  const [tab, setTab] = useState<AssignTasksTab>(initialTab);
  const taskCaseReturn = useMemo(() => assignTasksReturn(tab), [tab]);
  const { canUpdate, taskCaseLink } = useTaskCaseDetailsNavigation({ returnState: taskCaseReturn });
  const [allocationMode, setAllocationMode] = useState<'manual' | 'roundRobin'>('manual');
  const [allocationModeLoaded, setAllocationModeLoaded] = useState(false);
  const allocationModeTouchedRef = useRef(false);

  const [unassignedRows, setUnassignedRows] = useState<AssignTaskRow[]>([]);
  const [assignedRows, setAssignedRows] = useState<AssignTaskRow[]>([]);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [unassignedTotal, setUnassignedTotal] = useState(0);
  const [assignedTotal, setAssignedTotal] = useState(0);
  const [unassignedTotalPages, setUnassignedTotalPages] = useState(1);
  const [assignedTotalPages, setAssignedTotalPages] = useState(1);
  const [unassignedPage, setUnassignedPage] = useState(1);
  const [assignedPage, setAssignedPage] = useState(1);
  const [unassignedPageSize, setUnassignedPageSize] = useState(5);
  const [assignedPageSize, setAssignedPageSize] = useState(5);
  const [unassignedFilters, setUnassignedFilters] = useState<Record<string, string>>({});
  const [assignedFilters, setAssignedFilters] = useState<Record<string, string>>({});
  const [unassignedSort, setUnassignedSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({
    key: 'ref',
    dir: 'asc',
  });
  const [assignedSort, setAssignedSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({
    key: 'ref',
    dir: 'asc',
  });
  const [reallocationRows, setReallocationRows] = useState<AssignTaskRow[]>([]);
  const [reallocationLoading, setReallocationLoading] = useState(false);
  const [reallocationTotal, setReallocationTotal] = useState(0);
  const [reallocationTotalPages, setReallocationTotalPages] = useState(1);
  const [reallocationPage, setReallocationPage] = useState(1);
  const [reallocationPageSize, setReallocationPageSize] = useState(5);
  const [reallocationFilters, setReallocationFilters] = useState<Record<string, string>>({});
  const [reallocationSort, setReallocationSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({
    key: 'submittedAt',
    dir: 'asc',
  });

  const [selectedTask, setSelectedTask] = useState<AssignTaskRow | null>(null);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [linkedCasesModal, setLinkedCasesModal] = useState<{ caseRequestId: string } | null>(null);

  const [selectedReassignRows, setSelectedReassignRows] = useState<Map<string, AssignTaskRow>>(() => new Map());
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  const [bulkReassignContext, setBulkReassignContext] = useState<AssignTaskRow | null>(null);
  const [bulkReassignCancelConfirmOpen, setBulkReassignCancelConfirmOpen] = useState(false);
  const [selectAllFilteredLoading, setSelectAllFilteredLoading] = useState(false);
  const selectAllFilteredAbortRef = useRef<AbortController | null>(null);
  const selectAllHeaderCheckboxRef = useRef<HTMLInputElement | null>(null);

  const [saving, setSaving] = useState(false);
  const [bulkReassignSaving, setBulkReassignSaving] = useState(false);
  const [roundRobinBusy, setRoundRobinBusy] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const autoConvertedForModeRef = useRef<string | null>(null);

  const caseRequestKey = (row: AssignTaskRow) => (row.caseRequestId ?? row.id).trim();

  const changeTab = useCallback(
    (nextTab: AssignTasksTab) => {
      setTab(nextTab);
      setSearchParams({ tab: nextTab }, { replace: true });
      if (nextTab !== 'assigned') {
        selectAllFilteredAbortRef.current?.abort();
        selectAllFilteredAbortRef.current = null;
        setSelectAllFilteredLoading(false);
        setSelectedReassignRows(new Map());
      }
    },
    [setSearchParams],
  );

  const hasActiveAssignedFilters = useMemo(
    () => hasActiveColumnFilters(assignedFilters),
    [assignedFilters],
  );

  const allFilteredSelected = useMemo(() => {
    if (!hasActiveAssignedFilters || assignedTotal <= 0) return false;
    if (selectedReassignRows.size < assignedTotal) return false;
    return assignedRows.every((row) => {
      const caseId = (row.caseRequestId ?? row.id).trim();
      return caseId ? selectedReassignRows.has(caseId) : true;
    });
  }, [hasActiveAssignedFilters, assignedTotal, assignedRows, selectedReassignRows]);

  useEffect(() => {
    const el = selectAllHeaderCheckboxRef.current;
    if (!el) return;
    el.indeterminate =
      hasActiveAssignedFilters &&
      selectedReassignRows.size > 0 &&
      !allFilteredSelected;
  }, [hasActiveAssignedFilters, selectedReassignRows.size, allFilteredSelected]);

  /* Keep bulk selection limited to rows matching the current assigned-task filters. */
  useEffect(() => {
    if (tab !== 'assigned' || !hasActiveAssignedFilters) return;

    const controller = new AbortController();

    void (async () => {
      const items = await listAllAssignedTasks({
        pageSize: 100,
        columnFilters: assignedFilters,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      const allowedIds = new Set(
        items
          .map((row) => (row.caseRequestId ?? row.id).trim())
          .filter(Boolean),
      );

      setSelectedReassignRows((prev) => {
        if (prev.size === 0) return prev;
        const next = new Map<string, AssignTaskRow>();
        for (const [id, row] of prev) {
          if (!allowedIds.has(id)) continue;
          const fresh = items.find((item) => (item.caseRequestId ?? item.id).trim() === id);
          next.set(id, fresh ?? row);
        }
        return next.size === prev.size ? prev : next;
      });
    })();

    return () => controller.abort();
  }, [tab, hasActiveAssignedFilters, assignedFilters]);

  /* =========================
     DEFAULT ALLOCATION MODE (CMS-CCA-11)
  ========================= */
  useEffect(() => {
    void ensureExemptSubFileCategoryIdsLoaded();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    void getManualAllocationMode({ signal: controller.signal }).then((res) => {
      if (controller.signal.aborted) return;
      if (!allocationModeTouchedRef.current && res.success) {
        setAllocationMode(res.data.isManualAllocation ? 'manual' : 'roundRobin');
      }
      setAllocationModeLoaded(true);
    });

    return () => controller.abort();
  }, []);

  /* =========================
     LOAD DATA FROM API (per tab — avoids cross-tab pagination races)
  ========================= */
  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      setUnassignedLoading(true);
      try {
        const unassigned = await listUnassignedTasks({
          page: unassignedPage - 1,
          pageSize: unassignedPageSize,
          columnFilters: unassignedFilters,
          sortBy: unassignedSort.key,
          sortDir: unassignedSort.dir === 'asc' ? 'ASC' : 'DESC',
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (unassignedPage > unassigned.totalPages) {
          setUnassignedPage(unassigned.totalPages);
          return;
        }
        setUnassignedRows(unassigned.items);
        setUnassignedTotal(unassigned.total);
        setUnassignedTotalPages(unassigned.totalPages);
      } finally {
        if (!controller.signal.aborted) setUnassignedLoading(false);
      }
    })();

    return () => controller.abort();
  }, [
    unassignedPage,
    unassignedPageSize,
    unassignedFilters,
    unassignedSort.key,
    unassignedSort.dir,
    reloadToken,
  ]);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      setAssignedLoading(true);
      try {
        const assigned = await listAssignedTasks({
          page: assignedPage - 1,
          pageSize: assignedPageSize,
          columnFilters: assignedFilters,
          sortBy: assignedSort.key,
          sortDir: assignedSort.dir === 'asc' ? 'ASC' : 'DESC',
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (assignedPage > assigned.totalPages) {
          setAssignedPage(assigned.totalPages);
          return;
        }
        setAssignedRows(assigned.items);
        setAssignedTotal(assigned.total);
        setAssignedTotalPages(assigned.totalPages);
      } finally {
        if (!controller.signal.aborted) setAssignedLoading(false);
      }
    })();

    return () => controller.abort();
  }, [
    assignedPage,
    assignedPageSize,
    assignedFilters,
    assignedSort.key,
    assignedSort.dir,
    reloadToken,
  ]);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      setReallocationLoading(true);
      try {
        const reallocation = await listReallocationRequests({
          page: reallocationPage - 1,
          pageSize: reallocationPageSize,
          columnFilters: reallocationFilters,
          sortBy: reallocationSort.key,
          sortDir: reallocationSort.dir === 'asc' ? 'ASC' : 'DESC',
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (reallocationPage > reallocation.totalPages) {
          setReallocationPage(reallocation.totalPages);
          return;
        }
        setReallocationRows(reallocation.items);
        setReallocationTotal(reallocation.total);
        setReallocationTotalPages(reallocation.totalPages);
      } finally {
        if (!controller.signal.aborted) setReallocationLoading(false);
      }
    })();

    return () => controller.abort();
  }, [
    reallocationPage,
    reallocationPageSize,
    reallocationFilters,
    reallocationSort.key,
    reallocationSort.dir,
    reloadToken,
  ]);

  const rows =
    tab === 'unassigned' ? unassignedRows : tab === 'reallocation' ? reallocationRows : assignedRows;
  const loading =
    tab === 'unassigned' ? unassignedLoading : tab === 'reallocation' ? reallocationLoading : assignedLoading;
  const activeSort =
    tab === 'unassigned' ? unassignedSort : tab === 'reallocation' ? reallocationSort : assignedSort;

  const statusPillClass = (status: string) => {
    const s = status.trim().toLowerCase();
    if (s.includes('re-alloc') || s.includes('realloc')) return 'pill p-realloc';
    if (s.includes('pending')) return 'pill p-pend-task';
    if (s.includes('assigned')) return 'pill p-assigned';
    return 'pill p-aw';
  };

  const showAssignmentColumns = tab === 'assigned';
  const showConfigureAssignment =
    tab === 'reallocation' || (tab === 'unassigned' && allocationMode === 'manual');

  const toggleReassignSelection = useCallback((row: AssignTaskRow, checked: boolean) => {
    const caseId = (row.caseRequestId ?? row.id).trim();
    if (!caseId) return;
    setSelectedReassignRows((prev) => {
      const next = new Map(prev);
      if (checked) next.set(caseId, row);
      else next.delete(caseId);
      return next;
    });
  }, []);

  const toggleSelectAllFiltered = useCallback(
    async (checked: boolean) => {
      if (!hasActiveAssignedFilters) return;

      selectAllFilteredAbortRef.current?.abort();
      selectAllFilteredAbortRef.current = null;

      if (!checked) {
        setSelectAllFilteredLoading(false);
        setSelectedReassignRows(new Map());
        return;
      }

      const controller = new AbortController();
      selectAllFilteredAbortRef.current = controller;
      setSelectAllFilteredLoading(true);
      try {
        const items = await listAllAssignedTasks({
          pageSize: 100,
          columnFilters: assignedFilters,
          sortBy: assignedSort.key,
          sortDir: assignedSort.dir === 'asc' ? 'ASC' : 'DESC',
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        const next = new Map<string, AssignTaskRow>();
        for (const row of items) {
          const caseId = (row.caseRequestId ?? row.id).trim();
          if (caseId) next.set(caseId, row);
        }
        setSelectedReassignRows(next);
      } finally {
        if (!controller.signal.aborted) setSelectAllFilteredLoading(false);
        if (selectAllFilteredAbortRef.current === controller) {
          selectAllFilteredAbortRef.current = null;
        }
      }
    },
    [hasActiveAssignedFilters, assignedFilters, assignedSort.key, assignedSort.dir],
  );

  const openBulkReassign = useCallback(() => {
    const selectedRows = [...selectedReassignRows.values()];
    if (!selectedRows.length) {
      setFlashToast({ message: t('tasks.assign.selectCasesToReassign'), variant: 'error' });
      return;
    }

    const categoryKey = (r: AssignTaskRow) =>
      `${(r.fileCategoryId ?? '').trim()}|${(r.subFileCategoryId ?? '').trim()}`;
    const firstKey = categoryKey(selectedRows[0]!);
    const mixed = selectedRows.some((r) => categoryKey(r) !== firstKey);
    if (mixed) {
      setFlashToast({ message: t('tasks.assign.mixedCategoryReassignError'), variant: 'error' });
      return;
    }

    setBulkReassignContext(selectedRows[0]!);
    setBulkReassignOpen(true);
  }, [selectedReassignRows, t]);

  /* =========================
     TABLE COLUMNS (UI RESTORED)
  ========================= */
  const columns = useMemo<Column<AssignTaskRow>[]>(() => {
    const baseColumns: Column<AssignTaskRow>[] = [
      {
        key: 'ref',
        header: t('tasks.shared.fileRefNo'),
        getValue: (r) => r.ref,
        render: (r) => (
          <span className="rn">
            <Link
              {...taskCaseLink({ caseRequestId: r.id })}
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              {r.ref}
            </Link>
          </span>
        ),
      },
      { key: 'fileCategory', header: t('tasks.shared.fileCategory'), getValue: (r) => r.fileCategory },
      { key: 'subFileCategory', header: t('tasks.shared.subFileCategory'), getValue: (r) => r.subFileCategory },
      { key: 'agency', header: t('tasks.shared.submittedAgency'), getValue: (r) => r.agency },
      { key: 'caseNumber', header: t('tasks.shared.caseNumber'), getValue: (r) => r.caseNumber },
      {
        key: 'submittedAt',
        header: t('tasks.shared.submittedAt'),
        filterType: 'date-range',
        getValue: (r) => submittedAtToIsoDate(r.submittedAt),
        render: (r) => <span>{r.submittedAt}</span>,
      },
    ];

    const reallocationColumns: Column<AssignTaskRow>[] =
      tab === 'reallocation'
        ? [
            {
              key: 'reallocationRequestedAt',
              header: t('tasks.assign.reallocationRequestedAt'),
              searchable: false,
              getValue: (r) => r.reallocationRequestedAt ?? '-',
              render: (r) => <span>{r.reallocationRequestedAt ?? '-'}</span>,
            },
            {
              key: 'reasonToReallocate',
              header: t('tasks.assign.reasonToRequestReallocation'),
              getValue: (r) => r.reasonToReallocate ?? '-',
            },
            {
              key: 'reallocationRequestedBy',
              header: t('tasks.assign.reallocationRequestedBy'),
              getValue: (r) => r.reallocationRequestedBy ?? '-',
            },
          ]
        : [];

    const statusColumn: Column<AssignTaskRow> = {
      key: 'status',
      header: t('tasks.shared.taskStatus'),
      getValue: (r) => r.status,
      render: (r) => <span className={statusPillClass(r.status)}>{r.status}</span>,
    };

    const assignmentColumns: Column<AssignTaskRow>[] = showAssignmentColumns
      ? [
          {
            key: 'assignedOfficer',
            header: t('tasks.shared.assignedOfficer'),
            getValue: (r) => r.assignedOfficer ?? '-',
          },
          {
            key: 'allocationOfficer',
            header: t('tasks.shared.allocationOfficer'),
            getValue: (r) => r.allocationOfficer ?? '-',
          },
          {
            key: 'supervisor',
            header: t('tasks.shared.supervisor'),
            getValue: (r) => r.supervisor ?? '-',
          },
        ]
      : [];

    const actionsColumn: Column<AssignTaskRow> = {
      key: 'actions',
      header: t('common.actions'),
      getValue: () => '',
      searchable: false,
      sortable: false,
      render: (r) => (
        <div className="act-wrap">
          <Link
            {...taskCaseLink({
              caseRequestId: r.caseRequestId ?? r.id,
              userTaskId: r.workflowInstanceStageUserTaskId,
              viewMode: !canUpdate,
            })}
            className="act-b"
            data-tip={
              canUpdate ? t('tasks.shared.updateTaskCaseDetails') : t('tasks.shared.viewTaskCaseDetailsShort')
            }
            aria-label={
              canUpdate ? t('tasks.shared.updateTaskCaseDetails') : t('tasks.shared.viewTaskCaseDetailsShort')
            }
          >
            {canUpdate ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </Link>
          {showConfigureAssignment ? (
            <button
              type="button"
              className="act-b"
              data-tip={t('tasks.assign.configureAssignment')}
              aria-label={t('tasks.assign.configureAssignment')}
              onClick={() => {
                setSelectedTask(r);
                setAssignmentOpen(true);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
                <path d="M19 8v6" />
                <path d="M16 11h6" />
              </svg>
            </button>
          ) : null}
          <LinkedCasesActionButton
            connectedAgdFileRefAvailable={r.connectedAgdFileRefAvailable === true}
            caseRequestId={r.caseRequestId ?? r.id}
            onOpen={(caseRequestId) => setLinkedCasesModal({ caseRequestId })}
          />
        </div>
      ),
    };

    if (tab === 'reallocation') {
      return [...baseColumns, ...reallocationColumns, statusColumn, actionsColumn];
    }

    const configureAssignmentIcon = (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
        <path d="M19 8v6" />
        <path d="M16 11h6" />
      </svg>
    );

    const reassignColumn: Column<AssignTaskRow> = {
      key: 'selectToReassign',
      header: t('tasks.assign.selectToReassign'),
      getValue: () => '',
      searchable: false,
      sortable: false,
      headerClassName: 'dt-col-reassign',
      cellClassName: 'dt-col-reassign',
      renderHeader: () => (
        <span className="assign-reassign-th">
          <span className="assign-reassign-th-top">
            <span>{t('tasks.assign.selectToReassign')}</span>
            <ActionTipButton
              tooltip={t('tasks.assign.configureAssignment')}
              tipAlign="end"
              disabled={selectedReassignRows.size === 0 || bulkReassignSaving}
              onClick={openBulkReassign}
            >
              {configureAssignmentIcon}
            </ActionTipButton>
          </span>
          {hasActiveAssignedFilters ? (
            <label className="assign-reassign-select-all">
              <input
                ref={selectAllHeaderCheckboxRef}
                type="checkbox"
                checked={allFilteredSelected}
                disabled={selectAllFilteredLoading || assignedTotal === 0}
                aria-label={t('tasks.assign.selectAllFiltered')}
                onChange={(e) => {
                  void toggleSelectAllFiltered(e.target.checked);
                }}
              />
              <span>{t('tasks.assign.selectAllFiltered')}</span>
            </label>
          ) : null}
        </span>
      ),
      render: (r) => {
        const caseId = caseRequestKey(r);
        return (
          <input
            type="checkbox"
            checked={selectedReassignRows.has(caseId)}
            aria-label={t('tasks.assign.selectToReassignRow', { ref: r.ref })}
            onChange={(e) => toggleReassignSelection(r, e.target.checked)}
          />
        );
      },
    };

    if (tab === 'assigned') {
      return [...baseColumns, statusColumn, ...assignmentColumns, actionsColumn, reassignColumn];
    }

    return [...baseColumns, statusColumn, ...assignmentColumns, actionsColumn];
  }, [
    tab,
    showAssignmentColumns,
    showConfigureAssignment,
    canUpdate,
    taskCaseLink,
    selectedReassignRows,
    bulkReassignSaving,
    openBulkReassign,
    toggleReassignSelection,
    hasActiveAssignedFilters,
    allFilteredSelected,
    selectAllFilteredLoading,
    assignedTotal,
    toggleSelectAllFiltered,
    t,
  ]);

  /* =========================
     SAVE ASSIGNMENT (API)
  ========================= */
  const saveAssignment = async (details: {
    supervisorId?: string;
    stateAttorneyId?: string;
    caseRequestCategoryAllocationUserId: string;
    userId: string;
  }) => {
    if (!selectedTask) return;
    const workflowInstanceStageUserTaskId = selectedTask.workflowInstanceStageUserTaskId?.trim() ?? '';
    if (!workflowInstanceStageUserTaskId) return;

    setSaving(true);
    try {
      const res = await assignCategoryAllocationUser({
        caseRequestCategoryAllocationUserId: details.caseRequestCategoryAllocationUserId,
        userId: details.userId,
        workflowInstanceStageUserTaskId,
        supervisorId: details.supervisorId,
        stateAttorneyId: details.stateAttorneyId,
      });
      if (!res.success) {
        setFlashToast({ message: res.message || t('tasks.assign.assignmentFailed'), variant: 'error' });
        return;
      }
      setFlashToast({ message: t('tasks.assign.assignmentSuccess'), variant: 'success' });

      setAssignmentOpen(false);
      setSelectedTask(null);

      // Reload Unassigned / Assigned tabs and tab counts from the API.
      setReloadToken((t) => t + 1);
    } finally {
      setSaving(false);
    }
  };

  const beginCancel = () => setCancelConfirmOpen(true);
  const confirmCancel = () => {
    setAssignmentOpen(false);
    setCancelConfirmOpen(false);
    setSelectedTask(null);
  };

  const closeAssignmentDirectly = () => {
    setAssignmentOpen(false);
    setSelectedTask(null);
  };

  const saveBulkReassign = async (details: {
    reallocateNow: boolean;
    assignedUserId: string;
    supervisorId: string;
    stateAttorneyId: string;
  }): Promise<boolean> => {
    const caseRequestIds = [...selectedReassignRows.keys()];
    if (!caseRequestIds.length) return false;

    setBulkReassignSaving(true);
    try {
      const res = await bulkReallocateCaseRequests({
        caseRequestIds,
        reallocateNow: details.reallocateNow,
        assignedUserId: details.assignedUserId,
        supervisorId: details.supervisorId,
        stateAttorneyId: details.stateAttorneyId,
      });
      if (!res.success) {
        setFlashToast({ message: res.message || t('tasks.assign.bulkReassignFailed'), variant: 'error' });
        return false;
      }
      setFlashToast({ message: t('tasks.assign.bulkReassignSuccess'), variant: 'success' });
      setBulkReassignOpen(false);
      setBulkReassignContext(null);
      setSelectedReassignRows(new Map());
      if (!details.reallocateNow) {
        setTab('unassigned');
        setUnassignedPage(1);
      }
      setReloadToken((token) => token + 1);
      return true;
    } finally {
      setBulkReassignSaving(false);
    }
  };

  /* =========================
     ALLOCATION MODE SYNC (API) — CMS-CCA-4
     Fires for BOTH "Manual Allocation" and "Round Robin Allocation", passing
     is_manual_allocation accordingly, whenever the mode changes (toggle click
     or the initial default fetched from CMS-CCA-11) while there are unassigned tasks.
  ========================= */
  useEffect(() => {
    if (tab !== 'unassigned') return;
    if (!allocationModeLoaded) return;
    if (roundRobinBusy) return;
    if (unassignedTotal === 0) return;
    // Only sweep once per mode selection — a converted case can still match the
    // "unassigned" query afterward (e.g. its new assignee coincides with the officer),
    // which would otherwise re-trigger this effect and keep advancing it indefinitely.
    if (autoConvertedForModeRef.current === allocationMode) return;

    const isManual = allocationMode === 'manual';

    const run = async () => {
      autoConvertedForModeRef.current = allocationMode;
      setRoundRobinBusy(true);
      try {
        const ids: string[] = [];
        const fetchPageSize = 200;
        let page = 0;
        let totalPages = 1;

        while (page < totalPages) {
          const result = await listUnassignedTasks({
            page,
            pageSize: fetchPageSize,
            columnFilters: unassignedFilters,
            sortBy: unassignedSort.key,
            sortDir: unassignedSort.dir === 'asc' ? 'ASC' : 'DESC',
          });
          for (const row of result.items) {
            const id = row.caseRequestId ?? row.id;
            if (id) ids.push(id);
          }
          totalPages = result.totalPages;
          page += 1;
        }

        if (!ids.length) return;

        const res = await roundRobinAssignUsers({ caseRequestIds: ids, isManualAllocation: isManual });
        if (!res.success) {
          setFlashToast({ message: res.message, variant: 'error' });
          return;
        }
        // BE can return HTTP 200 / SUCCESS but still skip some tasks (e.g. incomplete
        // user categorization) — surface that as a non-blocking warning toast.
        if (res.data.warningMessage) {
          setFlashToast({ message: res.data.warningMessage, variant: 'warning' });
        }
        setReloadToken((t) => t + 1);
      } finally {
        setRoundRobinBusy(false);
      }
    };

    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allocationMode,
    allocationModeLoaded,
    unassignedTotal,
  ]);

  return (
    <>
      {/* HEADER */}
      <div className="bc">
        <span>{t('tasks.assign.breadcrumb')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('tasks.assign.title')}</div>
          <div className="pg-sub">
            {t('tasks.assign.subtitle')}
          </div>
        </div>
      </div>

      <div className="cr-table-wrap">
        <div className="cr-tabs-bar cr-tabs-bar--split">
          <div className="cr-tabs-left">
            <button
              type="button"
              className={`cr-tab ${tab === 'unassigned' ? 'on' : ''}`}
              onClick={() => changeTab('unassigned')}
            >
              {t('tasks.assign.unassignedTab')} <span className="tab-count">{unassignedTotal}</span>
            </button>

            <button
              type="button"
              className={`cr-tab ${tab === 'reallocation' ? 'on' : ''}`}
              onClick={() => changeTab('reallocation')}
            >
              {t('tasks.assign.reallocationTab')}{' '}
              <span className="tab-count">{reallocationTotal}</span>
            </button>

            <button
              type="button"
              className={`cr-tab ${tab === 'assigned' ? 'on' : ''}`}
              onClick={() => changeTab('assigned')}
            >
              {t('tasks.assign.assignedTab')} <span className="tab-count">{assignedTotal}</span>
            </button>
          </div>

          {tab === 'unassigned' ? (
            <div className="cr-tabs-right">
              <div className="wfa-radio-row" aria-label={t('tasks.assign.allocationModeLabel')}>
                <label>
                  <input
                    type="radio"
                    checked={allocationMode === 'manual'}
                    onChange={() => {
                      allocationModeTouchedRef.current = true;
                      setAllocationMode('manual');
                    }}
                    disabled={roundRobinBusy}
                  />
                  {t('tasks.assign.manualAllocation')}
                </label>

                <label>
                  <input
                    type="radio"
                    checked={allocationMode === 'roundRobin'}
                    onChange={() => {
                      allocationModeTouchedRef.current = true;
                      setAllocationMode('roundRobin');
                    }}
                    disabled={roundRobinBusy}
                  />
                  {t('tasks.assign.roundRobinAllocation')}
                </label>
              </div>
            </div>
          ) : null}
        </div>

        <div className="tab-panel on">
          <DataTable
            embedded
            title={t('tasks.shared.taskDetails')}
            columns={columns}
            rows={rows}
            loading={loading}
            rowKey={(r) => r.id}
            rowClassName={notificationRowHighlightClass}
            defaultRowsPerPage={5}
            previousLabel={t('tasks.shared.previousBack')}
            nextLabel={t('tasks.shared.nextForward')}
            serverPaging={
              tab === 'unassigned'
                ? {
                    totalCount: unassignedTotal,
                    totalPages: unassignedTotalPages,
                    page: unassignedPage,
                    pageSize: unassignedPageSize,
                    onPageChange: (p) => setUnassignedPage(p),
                    onPageSizeChange: (s) => setUnassignedPageSize(s),
                  }
                : tab === 'reallocation'
                  ? {
                      totalCount: reallocationTotal,
                      totalPages: reallocationTotalPages,
                      page: reallocationPage,
                      pageSize: reallocationPageSize,
                      onPageChange: (p) => setReallocationPage(p),
                      onPageSizeChange: (s) => setReallocationPageSize(s),
                    }
                  : {
                      totalCount: assignedTotal,
                      totalPages: assignedTotalPages,
                      page: assignedPage,
                      pageSize: assignedPageSize,
                      onPageChange: (p) => setAssignedPage(p),
                      onPageSizeChange: (s) => setAssignedPageSize(s),
                    }
            }
            onFiltersChange={(filters) => {
              if (tab === 'unassigned') {
                setUnassignedPage(1);
                setUnassignedFilters(filters);
              } else if (tab === 'reallocation') {
                setReallocationPage(1);
                setReallocationFilters(filters);
              } else {
                setAssignedPage(1);
                selectAllFilteredAbortRef.current?.abort();
                selectAllFilteredAbortRef.current = null;
                setSelectAllFilteredLoading(false);
                setAssignedFilters(filters);
                if (!hasActiveColumnFilters(filters)) {
                  setSelectedReassignRows(new Map());
                }
              }
            }}
            fetchColumnSuggestions={(col, prefix, signal) =>
              fetchAssignTaskColumnSuggestions(tab, col, prefix, signal)
            }
            serverSort={{
              key: activeSort.key,
              dir: activeSort.dir,
              onSortChange: (key, dir) => {
                if (tab === 'unassigned') {
                  setUnassignedSort({ key, dir });
                  setUnassignedPage(1);
                } else if (tab === 'reallocation') {
                  setReallocationSort({ key, dir });
                  setReallocationPage(1);
                } else {
                  setAssignedSort({ key, dir });
                  setAssignedPage(1);
                }
              },
            }}
          />
        </div>
      </div>

      {assignmentOpen && selectedTask ? (
      <ConfigureAssignmentModal
        isOpen
        idPrefix="cfg-assignment"
        fileCategoryId={selectedTask.fileCategoryId?.trim() ?? ''}
        subFileCategoryId={selectedTask.subFileCategoryId?.trim() ?? ''}
        fileCategoryName={selectedTask.fileCategory ?? ''}
        subFileCategoryName={selectedTask.subFileCategory ?? ''}
        workflowInstanceStageUserTaskId={selectedTask.workflowInstanceStageUserTaskId}
        saving={saving}
        onClose={closeAssignmentDirectly}
        onCancel={beginCancel}
        onSave={saveAssignment}
      />
      ) : null}

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('tasks.assign.cancelAssignmentTitle')}
        message={t('tasks.assign.cancelAssignmentMessage')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={confirmCancel}
      />

      {bulkReassignOpen && bulkReassignContext ? (
      <BulkReassignModal
        isOpen
        idPrefix="bulk-reassign"
        selectedCaseCount={selectedReassignRows.size}
        fileCategoryId={bulkReassignContext.fileCategoryId?.trim() ?? ''}
        subFileCategoryId={bulkReassignContext.subFileCategoryId?.trim() ?? ''}
        fileCategoryName={bulkReassignContext.fileCategory ?? ''}
        subFileCategoryName={bulkReassignContext.subFileCategory ?? ''}
        saving={bulkReassignSaving}
        onClose={() => {
          setBulkReassignOpen(false);
          setBulkReassignContext(null);
        }}
        onCancel={() => setBulkReassignCancelConfirmOpen(true)}
        onSave={saveBulkReassign}
      />
      ) : null}

      <ConfirmDialog
        isOpen={bulkReassignCancelConfirmOpen}
        title={t('tasks.assign.cancelAssignmentTitle')}
        message={t('tasks.assign.cancelAssignmentMessage')}
        onClose={() => setBulkReassignCancelConfirmOpen(false)}
        onConfirm={() => {
          setBulkReassignCancelConfirmOpen(false);
          setBulkReassignOpen(false);
          setBulkReassignContext(null);
        }}
      />

      <LinkedCasesModal
        open={linkedCasesModal != null}
        caseRequestId={linkedCasesModal?.caseRequestId ?? null}
        returnState={taskCaseReturn}
        onClose={() => setLinkedCasesModal(null)}
      />
    </>
  );
}